import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash-es'

import type { ApiEventMap, PitcherAPI, PitcherEvent, PitcherResponse, ApiOptions, PitcherEnv } from '@sdk/interfaces'
import { PitcherResponseStatus, PitcherMessageType } from '@sdk/interfaces'
import { EventEmitter } from '@sdk/utils/eventEmitter'
import { assert } from '@sdk/utils/assert'
import { snakeCaseKeys, camelCaseKeys } from '@lib/util/casing'
import { truncateObject } from '@sdk/utils/object'
import { isOriginValid } from '@lib/util/message-event'
import { setEnv, onlineRestApiHandlers } from '@sdk/api/onlineRestApiHandlers'
import { getTopPitcherWindow } from '@sdk/utils/window'

const TRUNCATE_LENGTH_TRIGGER = 10
// Responses returned verbatim even for `casing: 'camel'` consumers: AppsDB
// entries embed the opaque app-owned `data` blob, and camelizing would rewrite
// its keys — corrupting values consumers round-trip back into upserts and
// diverging from the REST path, which never transforms bodies.
const RAW_RESPONSE_METHODS: string[] = ['appsdb_get_entries', 'appsdb_upsert_entry', 'appsdb_delete_entry']
const RAW_PAYLOAD_METHODS: string[] = [
  // AppsDB `data` is an opaque app-owned blob — snake-casing its keys would corrupt stored payloads
  'appsdb_upsert_entry',
  'crm_create',
  'crm_upsert',
  'crm_describe',
  'crm_layout',
  'crm_smart_query',
  'crm_smart_object_metadata',
  'crm_smart_object_validation_rules',
  'crm_smart_object_layout',
  'crm_smart_upsert_objects',
  'crm_smart_delete_objects',
]

function updateOnlineHandlersEnv(env?: PitcherEnv) {
  env && setEnv(env)
}
export class LowLevelApi extends EventEmitter<ApiEventMap> implements PitcherAPI {
  constructor(options?: ApiOptions) {
    super()
    this.callbacks = {}
    this.options = options ?? {
      casing: 'snake',
      logLevel: 'off',
      errorLogger: () => {},
      fetchMode: 'postmessage',
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('message', (event) => {
        if (!isOriginValid(event)) return

        this.options.logLevel === 'debug' && console.log('message received', event)
        if (event.data.type === 'PITCHER_RESPONSE') {
          assert(event.data.request.id, 'event id is required')
          this.handleCallbackMessage(event.data.request.id, event.data)
        } else if (event.data.type === 'PITCHER_EVENT') {
          this.handleEventMessage(this.options.casing === 'camel' ? camelCaseKeys(event.data.event) : event.data.event)
        }
      })

      this.request('subscribe') // subscribe to events
    }
  }

  callbacks: {
    [k: string]: { callback: (res: PitcherResponse) => void; dispatched_at: number; type: string; payload: object }
  }

  private options: ApiOptions

  private handleCallbackMessage(id: string, body: PitcherResponse) {
    if (this.callbacks[id]) {
      this.callbacks[id].callback(body)
      // metrics
      this.options.logLevel &&
        ['info', 'debug'].includes(this.options.logLevel) &&
        console.log(
          `It took ${Date.now() - this.callbacks[id].dispatched_at} ms for the event ${this.callbacks[id].type}`,
        )
      // cleanup
      delete this.callbacks[id]
    }
  }

  private handleEventMessage(event: PitcherEvent) {
    this.emit('event', event)
  }

  public request(type: string, body: object | null = null): Promise<any> {
    const fetchMode: ApiOptions['fetchMode'] = this.options.fetchMode ?? 'postmessage'
    return new Promise((resolve, reject) => {
      const id = uuid()
      const callback = (res: PitcherResponse) => {
        this.options.logLevel === 'debug' && console.log(`Callback ${id} response:`, res)
        if (res.response.status === 'ok') {
          resolve(
            (this.options.casing === 'camel' && !RAW_RESPONSE_METHODS.includes(type)
              ? camelCaseKeys(res.response.body)
              : res.response.body) as PitcherResponse,
          )

          if (type === 'get_env') updateOnlineHandlersEnv(res.response.body as PitcherEnv | undefined)
        } else if (res.response.status === 'error') {
          if (this.options.errorLogger) {
            this.options.errorLogger({
              type: res.request.type,
              message: `Pitcher JS API call failure: ${res.request.type}`,
              js_api_response: JSON.stringify(truncateObject(res, 3, TRUNCATE_LENGTH_TRIGGER)),
            })
          }
          reject(
            this.options.casing === 'camel' && !RAW_RESPONSE_METHODS.includes(type)
              ? camelCaseKeys(res.response.body)
              : res.response.body,
          )
        } else {
          throw new Error('unsupported response status')
        }
      }
      const shouldSkipSnakeCase = RAW_PAYLOAD_METHODS.includes(type)
      const payload = shouldSkipSnakeCase ? cloneDeep(body) : cloneDeep(snakeCaseKeys(body)) // default: snake case for impact / cloneDeep to create serializable object

      this.callbacks[id] = {
        dispatched_at: Date.now(),
        type,
        payload: payload ?? {},
        callback,
      }

      if (fetchMode === 'prefer-online' && type && onlineRestApiHandlers[type]) {
        onlineRestApiHandlers[type](payload)
          .then((data: any) => {
            if (this.callbacks[id]) {
              this.callbacks[id].callback({
                type: PitcherMessageType.RESPONSE,
                request: { id, type },
                response: { status: PitcherResponseStatus.OK, body: data },
              } satisfies PitcherResponse)
              delete this.callbacks[id]
            }
          })
          .catch((error: any) => {
            if (this.callbacks[id]) {
              this.callbacks[id].callback({
                type: PitcherMessageType.RESPONSE,
                request: { id, type },
                response: { status: PitcherResponseStatus.ERROR, body: error },
              } satisfies PitcherResponse)
              delete this.callbacks[id]
            }
          })
      } else {
        // Post message to top ancestor window
        const targetWindow = getTopPitcherWindow(window)
        if (typeof targetWindow !== 'undefined' && targetWindow) {
          targetWindow.postMessage(
            {
              type: 'PITCHER_REQUEST',
              request: {
                type,
                id,
                body: payload,
              },
            },
            '*',
          )
        }
      }
    })
  }
}
